import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { AppLayout } from '../components/AppLayout'
import { StoneImage } from '../components/StoneImage'
import { CertLink } from '../components/CertLink'
import { formatPrice, formatPerCarat, formatCarat } from '../lib/format'
import { sharesApi } from '../api/endpoints'
import { ApiError, type ShareView as Share, type ShareStone, type Diamond } from '../api/types'

/**
 * /share/:id — PUBLIC page for a "share with my markup" list (#40). No sign-in:
 * a broker sends this link to a customer. Prices are already marked up
 * server-side; the stock id is shown only if the sharer opted in. A CSV export
 * (Excel-openable) mirrors the VDB "download the list" flow.
 */
export function ShareView() {
  const { id = '' } = useParams()
  const [share, setShare] = useState<Share | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    sharesApi.get(id)
      .then((s) => { if (!cancelled) { setShare(s); setError(null) } })
      .catch((e) => { if (!cancelled) setError(e instanceof ApiError && e.status === 404 ? 'This shared list was not found or has expired.' : 'Could not load this shared list.') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [id])

  return (
    <AppLayout>
      <div className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gold">Shared selection</p>
            <h1 className="mt-1 font-serif text-3xl font-bold tracking-tight text-ink">
              {share?.title || 'Diamond selection'}
            </h1>
            {share?.note && <p className="mt-2 max-w-2xl text-sm text-muted leading-relaxed">{share.note}</p>}
            {share && (
              <p className="mt-2 text-[13px] text-soft tabular">
                {share.count} stone{share.count === 1 ? '' : 's'}
                {share.showStockId ? '' : ' · stock references hidden'}
              </p>
            )}
          </div>
          {share && share.stones.length > 0 && (
            <button
              type="button"
              onClick={() => downloadCsv(share)}
              className="inline-flex items-center gap-2 h-10 shrink-0 rounded-full bg-navy px-5 text-sm font-semibold text-white shadow-card hover:bg-navy/90 active:scale-[0.98] transition-all"
              data-testid="share-export"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download (Excel/CSV)
            </button>
          )}
        </div>

        {loading ? (
          <div className="rounded-card border border-border bg-paper p-10 text-center text-sm text-muted">Loading shared list…</div>
        ) : error ? (
          <div className="rounded-card border border-danger/30 bg-danger/5 p-6 text-sm text-danger">{error}</div>
        ) : !share || share.stones.length === 0 ? (
          <div className="rounded-card border border-border bg-paper p-10 text-center text-sm text-muted">This shared list is empty.</div>
        ) : (
          <>
            {/* Card grid on small screens; table on md+ (dense, like the catalog). */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:hidden gap-4">
              {share.stones.map((s, i) => <ShareCard key={i} s={s} showId={share.showStockId} />)}
            </div>
            <div className="hidden lg:block overflow-x-auto rounded-card border border-border bg-paper shadow-card">
              <table className="min-w-[1060px] w-full text-[12.5px] text-ink tabular">
                <thead className="bg-cream text-[10.5px] font-semibold uppercase tracking-[0.1em] text-gold">
                  <tr>
                    <th className="px-3 py-3 text-left">#</th>
                    <th className="px-2 py-3 text-left">Image</th>
                    {share.showStockId && <th className="px-2 py-3 text-left">Stock</th>}
                    <th className="px-2 py-3 text-left">Shape</th>
                    <th className="px-2 py-3 text-right">Carat</th>
                    <th className="px-2 py-3 text-center">Color</th>
                    <th className="px-2 py-3 text-center">Clarity</th>
                    <th className="px-2 py-3 text-center">Cut</th>
                    <th className="px-2 py-3 text-center">Pol</th>
                    <th className="px-2 py-3 text-center">Sym</th>
                    <th className="px-2 py-3 text-center">Fluo</th>
                    <th className="px-2 py-3 text-center">Lab</th>
                    <th className="px-2 py-3 text-left">Cert No</th>
                    <th className="px-2 py-3 text-left">Measurements</th>
                    <th className="px-2 py-3 text-right">$/ct</th>
                    <th className="px-2 py-3 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {share.stones.map((s, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-3 py-2.5 text-soft">{i + 1}</td>
                      <td className="px-2 py-2">
                        <div className="relative h-9 w-9 shrink-0 overflow-hidden rounded-full ring-1 ring-border bg-gradient-to-br from-[#faf7f1] to-[#eef1f6]">
                          <StoneImage stone={asDiamond(s)} badge={false} />
                        </div>
                      </td>
                      {share.showStockId && <td className="px-2 py-2.5 font-semibold">{s.displayId || '—'}</td>}
                      <td className="px-2 py-2.5">{s.shape || '—'}</td>
                      <td className="px-2 py-2.5 text-right tabular-nums">{formatCarat(s.carat)}</td>
                      <td className="px-2 py-2.5 text-center">{s.color || '—'}</td>
                      <td className="px-2 py-2.5 text-center">{s.clarity || '—'}</td>
                      <td className="px-2 py-2.5 text-center">{s.cut || '—'}</td>
                      <td className="px-2 py-2.5 text-center text-muted">{s.polish || '—'}</td>
                      <td className="px-2 py-2.5 text-center text-muted">{s.symmetry || '—'}</td>
                      <td className="px-2 py-2.5 text-center text-muted">{s.fluorescence || '—'}</td>
                      <td className="px-2 py-2.5 text-center">{s.lab || '—'}</td>
                      <td className="px-2 py-2.5 truncate max-w-[140px] text-muted" title={s.certNo || ''}>
                        <CertLink lab={s.lab} certNo={s.certNo} />
                      </td>
                      <td className="px-2 py-2.5 whitespace-nowrap text-muted tabular-nums">{measStr(s) || '—'}</td>
                      <td className="px-2 py-2.5 text-right font-semibold tabular-nums">{formatPerCarat(s.pricePerCarat)}</td>
                      <td className="px-2 py-2.5 text-right font-bold tabular-nums">{formatPrice(s.priceUSD)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  )
}

/** Adapt a ShareStone to the shape StoneImage needs (shape + media for its fallback chain).
 * Exported for the mobile shell's ShareViewScreen (same data, phone layout). */
export function asDiamond(s: ShareStone): Diamond {
  return { stockId: s.displayId || 'stone', shape: s.shape, media: s.media } as unknown as Diamond
}

/** "L × W × H" measurement string, or '' when the feed didn't carry it. */
export function measStr(s: ShareStone): string {
  const m = s.measurements
  if (!m || m.length == null) return ''
  return `${m.length} × ${m.width} × ${m.height}`
}

function ShareCard({ s, showId }: { s: ShareStone; showId: boolean }) {
  return (
    <div className="rounded-card border border-border bg-paper shadow-card overflow-hidden">
      <div className="relative aspect-square w-full overflow-hidden bg-gradient-to-br from-cream to-[#eef1f6]">
        <StoneImage stone={asDiamond(s)} />
      </div>
      <div className="p-3.5 space-y-1.5">
        {showId && s.displayId && <p className="text-sm font-semibold text-ink truncate">#{s.displayId}</p>}
        <p className="text-xs text-muted truncate">
          {s.shape} · {formatCarat(s.carat)}{s.color ? ` · ${s.color}` : ''}{s.clarity ? ` ${s.clarity}` : ''}
        </p>
        <div className="flex items-baseline justify-between pt-2 mt-1 border-t border-border">
          <p className="text-base font-bold text-ink tabular">{formatPerCarat(s.pricePerCarat)}</p>
          <p className="text-[11px] text-soft tabular">{formatPrice(s.priceUSD)} total</p>
        </div>
      </div>
    </div>
  )
}

/** Build a CSV (Excel-openable) from the shared stones and trigger a download.
 * Exported for the mobile shell's ShareViewScreen — one CSV shape everywhere. */
export function downloadCsv(share: Share): void {
  const headers = ['#', ...(share.showStockId ? ['Stock'] : []), 'Shape', 'Carat', 'Color', 'Clarity', 'Cut', 'Polish', 'Symmetry', 'Fluorescence', 'Lab', 'Cert No', 'Measurements (mm)', '$/ct', 'Total (USD)']
  const cell = (v: unknown) => {
    const s = v == null ? '' : String(v)
    // \r included: a lone carriage return from a feed value would otherwise
    // escape unquoted and split the row in Excel.
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const rows = share.stones.map((s, i) => [
    i + 1,
    ...(share.showStockId ? [s.displayId ?? ''] : []),
    s.shape ?? '', s.carat, s.color ?? '', s.clarity ?? '', s.cut ?? '', s.polish ?? '', s.symmetry ?? '', s.fluorescence ?? '', s.lab ?? '', s.certNo ?? '', measStr(s), s.pricePerCarat, s.priceUSD,
  ])
  const csv = [headers, ...rows].map((r) => r.map(cell).join(',')).join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${(share.title || 'diaex-selection').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.csv`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
